import { useEffect, useMemo, useState } from 'react';
import { BlockTitle, Icon, List, ListInput, ListItem, PageContent } from 'framework7-react';
import { fail } from '@/features/menu/utils/product-page-actions';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useT } from '@/i18n';
import AllergenCodeRow from './AllergenCodeRow';

type Kind = 'ALLERGEN' | 'ADDITIVE' | 'DIETARY';
type Entry = { shortKey: string; description: string; kind: Kind };

const KIND_ORDER: Kind[] = ['ALLERGEN', 'ADDITIVE', 'DIETARY'];

export default function AllergenCodesEditContent({
  open,
  onClose,
  table,
  initial,
  onSubmit,
  submitRef,
}: {
  open: boolean;
  onClose: () => void;
  table: Entry[];
  initial: readonly string[];
  onSubmit: (codes: string[], done: () => void, err: (e: unknown) => void) => void;
  submitRef: { current: () => void };
}) {
  const t = useT();
  const a = t.menu.allergenLabeling;
  const [sel, setSel] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) setSel([...initial]);
  }, [open, initial]);

  useUnsavedGuard(open && JSON.stringify([...sel].sort()) !== JSON.stringify([...initial].sort()));
  submitRef.current = () => onSubmit(sel, onClose, fail);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return KIND_ORDER.map((kind) => ({
      kind,
      items: table
        .filter((e) => e.kind === kind)
        .filter(
          (e) =>
            !q || e.shortKey.toLowerCase().includes(q) || e.description.toLowerCase().includes(q),
        ),
    })).filter((g) => g.items.length > 0);
  }, [table, query]);

  const toggle = (k: string) =>
    setSel((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  return (
    <PageContent>
      <BlockTitle>{t.menu.selectedCount(sel.length)}</BlockTitle>
      <List strongIos strongMd insetIos insetMd>
        <ListInput
          type="text"
          placeholder={t.menu.productForm.searchAllergenCodes}
          value={query}
          clearButton
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
        >
          <Icon slot="media" f7="search" />
        </ListInput>
      </List>

      {groups.map((g) => (
        <div key={g.kind}>
          <BlockTitle>{a.kindLabel(g.kind)}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            {g.items.map((e) => {
              const on = sel.includes(e.shortKey);
              return (
                <ListItem
                  key={e.shortKey}
                  link="#"
                  noChevron
                  onClick={(ev: React.MouseEvent) => {
                    ev.preventDefault();
                    toggle(e.shortKey);
                  }}
                  data-testid={`allergen-code-option-${e.shortKey}`}
                >
                  <div slot="title">
                    <AllergenCodeRow code={e.shortKey} description={e.description} />
                  </div>
                  {on && (
                    <Icon
                      slot="after"
                      f7="checkmark_alt"
                      size={20}
                      style={{ color: 'var(--f7-theme-color)' }}
                    />
                  )}
                </ListItem>
              );
            })}
          </List>
        </div>
      ))}
    </PageContent>
  );
}
